
import { useCallback } from 'react';
import { SimulationConfig, Ball, MorphData, ShapeType, TrailCircle, CollisionEvent, BlastEvent, EscapeRing, SpaceSpiralRoundConfig, BallPiece, RhythmicSimulationState } from '../types';
import { TrailCirclePool } from '../services/TrailCirclePool';
import { SHAPES } from '../constants';
import { AUDIO } from '../services/AudioService';
import { PhysicsSystem } from '../logic/physics/PhysicsSystem';
import { BallPool } from '../services/BallPool';
import { SpaceSpiralLogic } from '../logic/modes/SpaceSpiralLogic';
import { RhythmicLogic } from '../logic/modes/RhythmicLogic';
import { RhythmicEscapeLogic } from '../logic/modes/RhythmicEscapeLogic';
import { BasicModesLogic } from '../logic/modes/BasicModesLogic';
import { EscapeLogic } from '../logic/modes/EscapeLogic';
import { BallBreakAnimation } from '../logic/animations/BallBreakAnimation';
import { SpectrumLogic } from '../logic/SpectrumLogic';
import { RhythmicMultiSimLogic } from '../logic/RhythmicMultiSimLogic';
import { RhythmicSingleSimLogic } from '../logic/RhythmicSingleSimLogic';
import { LoopPhysicsLogic } from '../logic/LoopPhysicsLogic';

export const useSimulationLoop = (
  configRef: React.MutableRefObject<SimulationConfig>,
  isPlaying: boolean,
  ballsRef: React.MutableRefObject<Ball[]>,
  trailCirclesRef: React.MutableRefObject<TrailCircle[]>,
  collisionEventsRef: React.MutableRefObject<CollisionEvent[]>,
  blastEventsRef: React.MutableRefObject<BlastEvent[]>,
  escapeRingsRef: React.MutableRefObject<EscapeRing[]>,
  lastTimeRef: React.MutableRefObject<number>,
  hueRef: React.MutableRefObject<number>,
  timeScaleRef: React.MutableRefObject<number>,
  isRotationStoppedRef: React.MutableRefObject<boolean>,
  shakeRef: React.MutableRefObject<number>,
  counterPopRef: React.MutableRefObject<number>,
  timeRemainingRef: React.MutableRefObject<number>,
  morphTimerRef: React.MutableRefObject<number>,
  morphStateRef: React.MutableRefObject<{ active: boolean, from: ShapeType, to: ShapeType, progress: number }>,
  escapeAccelerationBoostRef: React.MutableRefObject<number>,
  containerRadiusRef: React.MutableRefObject<number>,
  rhythmicTimeScaleRef: React.MutableRefObject<number>,
  rhythmicTimeScaleRateRef: React.MutableRefObject<number>,
  currentRoundRef: React.MutableRefObject<number>,
  roundStartTimeRef: React.MutableRefObject<number>,
  roundOverridesRef: React.MutableRefObject<SpaceSpiralRoundConfig | null>,
  physicsSystemRef: React.MutableRefObject<PhysicsSystem>,
  workerRef: React.MutableRefObject<Worker | null>,
  ballPoolRef: React.MutableRefObject<BallPool>,
  trailCirclePoolRef: React.MutableRefObject<TrailCirclePool>,
  speedIncreaseRateRef: React.MutableRefObject<number>,
  ballGrowthRateRef: React.MutableRefObject<number>,
  containerShrinkRateRef: React.MutableRefObject<number>,
  rhythmicBreakPiecesRef: React.MutableRefObject<BallPiece[]>,
  persistentSpectrumBoostRef: React.MutableRefObject<Float32Array>,
  persistentSpectrumTargetRef: React.MutableRefObject<Float32Array>,
  rhythmicResetTimeoutRef: React.MutableRefObject<NodeJS.Timeout | null>,
  rhythmicIndexRef: React.MutableRefObject<number>,
  rhythmicSimulationsStateRef: React.MutableRefObject<RhythmicSimulationState[]>,
  hyperSpeedStateRef: React.MutableRefObject<{ active: boolean, startTime: number }>,
  zoomLevelRef: React.MutableRefObject<number>,
  requestRef: React.MutableRefObject<number>,
  simTimeRef: React.MutableRefObject<number>,
  setElapsedTime: React.Dispatch<React.SetStateAction<number>>,
  setBounceCount: React.Dispatch<React.SetStateAction<number>>,
  setBallCount: React.Dispatch<React.SetStateAction<number>>,
  setConfig: React.Dispatch<React.SetStateAction<SimulationConfig>>,
  setIsPlaying: React.Dispatch<React.SetStateAction<boolean>>,
  triggerBlast: (b: {pos: {x: number, y: number}, color: string, radius: number}, cmr?: number, gapAngle?: number, morph?: MorphData, animType?: string) => void,
  playCollisionSound: (isDestruction?: boolean) => void,
  initSimulation: (resetRounds?: boolean) => void,
  draw: () => void,
  rhythmicEscapeShieldActiveRef: React.MutableRefObject<boolean>,
  rhythmicEscapeShieldBouncesRef: React.MutableRefObject<number>,
  rhythmicEscapeShieldTimeRef: React.MutableRefObject<number>,
  rhythmicEscapeShieldAngleRef: React.MutableRefObject<number>
) => {
  const update = useCallback(() => {
    const cfg = configRef.current; 
    let effectiveCfg = { ...cfg };
    if (effectiveCfg.mode === 'SPACE_SPIRAL' && roundOverridesRef.current) {
        Object.assign(effectiveCfg, roundOverridesRef.current);
    }

    if (requestRef.current) cancelAnimationFrame(requestRef.current);
    requestRef.current = requestAnimationFrame(update);

    if (!isPlaying) {
        draw(); lastTimeRef.current = Date.now(); return;
    }
    
    const now = Date.now(), deltaTime = Math.max(1, now - lastTimeRef.current);
    lastTimeRef.current = now; 

    // 1. Spectrum Update
    if (effectiveCfg.rhythmicSpectrumEnabled) {
        SpectrumLogic.update(deltaTime, effectiveCfg, AUDIO.getFrequencyData(), persistentSpectrumBoostRef.current, persistentSpectrumTargetRef.current, ballsRef.current);
    }
    
    // 2. State & Hue Updates
    if (ballGrowthRateRef.current !== effectiveCfg.ballGrowthRate && !effectiveCfg.ballGrowthAcceleration) ballGrowthRateRef.current = effectiveCfg.ballGrowthRate;
    if (speedIncreaseRateRef.current !== effectiveCfg.speedIncreaseRate && !effectiveCfg.speedAcceleration) speedIncreaseRateRef.current = effectiveCfg.speedIncreaseRate;
    if (containerShrinkRateRef.current !== effectiveCfg.containerShrinkRate && !effectiveCfg.containerShrinkAcceleration) containerShrinkRateRef.current = effectiveCfg.containerShrinkRate;

    if (effectiveCfg.rgbLighting) hueRef.current = (hueRef.current + effectiveCfg.hueCycleSpeed) % 360;
    if (shakeRef.current > 0) { shakeRef.current *= effectiveCfg.shakeDecayRate; if (shakeRef.current < 0.1) shakeRef.current = 0; }
    if (counterPopRef.current > 1.0) { counterPopRef.current -= effectiveCfg.popDecayRate * (deltaTime / 16.67); if (counterPopRef.current < 1.0) counterPopRef.current = 1.0; }
    collisionEventsRef.current = collisionEventsRef.current.filter(e => now - e.startTime < e.duration);
    blastEventsRef.current = blastEventsRef.current.filter(e => now - e.startTime < e.duration);

    // 3. Morph & Modes Logic
    BasicModesLogic.handleMorph(deltaTime, effectiveCfg, morphTimerRef, morphStateRef, SHAPES, setConfig);

    if (effectiveCfg.mode === 'SPACE_SPIRAL' && effectiveCfg.spaceSpiralEnableRounds) {
        if (SpaceSpiralLogic.handleRoundLogic(now, roundStartTimeRef.current, effectiveCfg, ballsRef.current, 0, 0, currentRoundRef, setIsPlaying, () => { roundStartTimeRef.current = Date.now(); initSimulation(); })) return;
    }

    const rotRad = (effectiveCfg.containerRotation || 0) * Math.PI / 180;
    const morphBaseRadius = (effectiveCfg.mode === 'RHYTHMIC_ESCAPE' ? effectiveCfg.rhythmicEscapeShieldRingRadius : effectiveCfg.containerRadius);
    const morphData: MorphData = morphStateRef.current.active ? { from: morphStateRef.current.from, to: morphStateRef.current.to, factor: morphStateRef.current.progress, baseRadius: morphBaseRadius } : { from: effectiveCfg.containerShape, to: effectiveCfg.containerShape, factor: 0, baseRadius: morphBaseRadius };

    if (effectiveCfg.speedIncreaseMode === 'TIME') {
        timeScaleRef.current += speedIncreaseRateRef.current * (deltaTime / 1000);
        if (effectiveCfg.speedAcceleration !== 0) speedIncreaseRateRef.current += effectiveCfg.speedAcceleration * (deltaTime / 1000);
    }

    if (effectiveCfg.escapeAccelerationEnabled && escapeAccelerationBoostRef.current > 0) {
        escapeAccelerationBoostRef.current = Math.max(0, escapeAccelerationBoostRef.current - effectiveCfg.escapeAccelerationDecay * (deltaTime / 16.67));
    } else if (!effectiveCfg.escapeAccelerationEnabled) escapeAccelerationBoostRef.current = 0;

    const currentTS = (timeScaleRef.current * effectiveCfg.masterSpeed) + escapeAccelerationBoostRef.current;
    const melodyTS = (effectiveCfg.mode === 'RHYTHMIC' && effectiveCfg.rhythmicBallSpeedDecoupled) ? effectiveCfg.masterSpeed : currentTS;
    simTimeRef.current += deltaTime * melodyTS;
    setElapsedTime(prev => prev + deltaTime * melodyTS);

    if (rhythmicBreakPiecesRef.current.length > 0) {
        rhythmicBreakPiecesRef.current = BallBreakAnimation.updatePieces(rhythmicBreakPiecesRef.current, deltaTime, effectiveCfg.rhythmicBreakGravity, now);
    }

    // 4. Mode Specific Updates
    if (effectiveCfg.mode === 'RHYTHMIC') {
        if (effectiveCfg.rhythmicMultiSimEnabled) {
            RhythmicMultiSimLogic.updateAll(rhythmicSimulationsStateRef.current, effectiveCfg, deltaTime, simTimeRef.current, rotRad, hueRef.current, morphData, collisionEventsRef, persistentSpectrumBoostRef, persistentSpectrumTargetRef, shakeRef, triggerBlast, rhythmicBreakPiecesRef, rhythmicResetTimeoutRef, ballPoolRef.current, trailCirclesRef.current, trailCirclePoolRef.current, setBounceCount);
            ballsRef.current = rhythmicSimulationsStateRef.current.map(s => s.ball).filter(b => b !== null) as Ball[];
        } else {
            if (ballsRef.current.length > 1) { 
                ballsRef.current.splice(1).forEach(b => ballPoolRef.current.release(b));
                setBallCount(1);
            }
            if (ballsRef.current[0]) RhythmicSingleSimLogic.update(ballsRef.current[0], effectiveCfg, deltaTime, containerRadiusRef, ballGrowthRateRef, rhythmicTimeScaleRef, rhythmicTimeScaleRateRef, rotRad, hueRef.current, morphData, collisionEventsRef, persistentSpectrumBoostRef, persistentSpectrumTargetRef, simTimeRef.current, currentTS, shakeRef, triggerBlast, rhythmicBreakPiecesRef, rhythmicResetTimeoutRef, rhythmicIndexRef, initSimulation, setBounceCount, rhythmicSimulationsStateRef, setConfig, trailCirclesRef.current, trailCirclePoolRef.current);
        }
        setBallCount(ballsRef.current.length);
        draw(); return; 
    }

    if (effectiveCfg.mode === 'RHYTHMIC_ESCAPE') {
        if (ballsRef.current[0]) {
            RhythmicEscapeLogic.update(
                ballsRef.current[0],
                effectiveCfg,
                deltaTime,
                0, 0,
                escapeRingsRef,
                rhythmicEscapeShieldActiveRef,
                rhythmicEscapeShieldBouncesRef,
                rhythmicEscapeShieldTimeRef,
                rhythmicEscapeShieldAngleRef,
                rhythmicTimeScaleRef,
                rhythmicTimeScaleRateRef,
                rotRad,
                hueRef.current,
                morphData,
                collisionEventsRef,
                persistentSpectrumTargetRef,
                trailCirclesRef,
                trailCirclePoolRef,
                now,
                currentTS,
                (i) => { shakeRef.current = Math.max(shakeRef.current, i); },
                triggerBlast,
                rhythmicIndexRef,
                () => setBounceCount(prev => prev + 1),
                (ring, idx) => {
                    const ringColor = `hsl(${(hueRef.current + ring.hue) % 360}, 100%, 50%)`;
                    triggerBlast({ pos: { x: 0, y: 0 }, color: ringColor, radius: ring.radius }, ring.radius, ring.angle, morphData);
                    counterPopRef.current = 1.35;
                }
            );
        }

        const rotationActive = !isRotationStoppedRef.current && effectiveCfg.escapeRotationMode !== 'NONE';
        if (rotationActive) {
            rhythmicEscapeShieldAngleRef.current += effectiveCfg.escapeRotationSpeed * currentTS * 0.01;
        }

        // Update rings rotation/shrinking normally
        escapeRingsRef.current.forEach((ring, idx) => {
            const shieldRadius = rhythmicEscapeShieldActiveRef.current ? (effectiveCfg.rhythmicEscapeShieldRingRadius || 0) : 0;
            // Use absolute index to prevent overlapping when innermost rings are destroyed
            const absoluteIdx = idx + (effectiveCfg.escapeRingCount - escapeRingsRef.current.length);
            EscapeLogic.updateRings(ring, effectiveCfg, currentTS, absoluteIdx, isRotationStoppedRef.current, false, escapeRingsRef.current.length, shieldRadius);
        });

        setBallCount(ballsRef.current.length);
        draw(); return;
    }

    BasicModesLogic.handleGrowthAndShrink(ballsRef.current, effectiveCfg, deltaTime, containerRadiusRef, ballGrowthRateRef, containerShrinkRateRef, morphData, 0, 0, (b) => { triggerBlast({ pos: { x: 0, y: 0 }, color: b.color, radius: b.radius }, containerRadiusRef.current, undefined, morphData, 'PHYSICS_SHATTER'); setIsPlaying(false); setTimeout(() => { initSimulation(true); setIsPlaying(true); }, 2000); });
    
    BasicModesLogic.handleZoom(effectiveCfg, escapeRingsRef.current.length, zoomLevelRef);

    if (effectiveCfg.mode === 'ESCAPE') {
        EscapeLogic.handleHyperSpeed(ballsRef.current, escapeRingsRef.current, effectiveCfg, hyperSpeedStateRef.current, now);
        escapeRingsRef.current.forEach((ring, idx) => {
            const oldRadius = ring.radius;
            EscapeLogic.updateRings(ring, effectiveCfg, currentTS, idx, isRotationStoppedRef.current, hyperSpeedStateRef.current.active, escapeRingsRef.current.length);
            if (idx === 0 && ring.radius <= 10.1 && oldRadius > 10.1) {
                if (ballsRef.current[0]) {
                    triggerBlast({ pos: { x: 0, y: 0 }, color: `hsl(${(hueRef.current + ring.hue) % 360}, 100%, 50%)`, radius: 10 }, 10, ring.angle);
                    if (effectiveCfg.escapeTimeChallengeMode) timeRemainingRef.current += Math.max(0, effectiveCfg.escapeTimeAddition + (effectiveCfg.escapeTimeAdditionStep * (effectiveCfg.escapeRingCount - escapeRingsRef.current.length)));
                    escapeRingsRef.current.shift(); setBounceCount(prev => prev + 1); counterPopRef.current = 1.35;
                }
            }
        });
    }
    
    // 5. Physics Substeps
    const spawns = LoopPhysicsLogic.handleSubsteps(deltaTime, effectiveCfg, ballsRef.current, 0, 0, rotRad, hueRef.current, now, currentTS, morphData, collisionEventsRef.current, persistentSpectrumBoostRef.current, persistentSpectrumTargetRef.current, containerRadiusRef, ballGrowthRateRef, containerShrinkRateRef, timeScaleRef, speedIncreaseRateRef, shakeRef, counterPopRef, setBounceCount, setIsPlaying, initSimulation, triggerBlast, playCollisionSound, escapeRingsRef, isRotationStoppedRef.current, hyperSpeedStateRef.current, timeRemainingRef, escapeAccelerationBoostRef, physicsSystemRef.current, ballPoolRef.current, trailCirclesRef.current, trailCirclePoolRef.current, workerRef);

    if (spawns.length > 0) {
      spawns.forEach(s => {
        if (ballsRef.current.length < effectiveCfg.maxBalls) {
            const b = ballPoolRef.current.get(Math.random().toString(), s.pos, { x: (Math.random()-0.5)*10, y: (Math.random()-0.5)*10 }, effectiveCfg.ballRadius, s.color, effectiveCfg.ballMass, effectiveCfg.ballStyle);
            ballsRef.current.push(b);
            if (workerRef.current) workerRef.current.postMessage({ type: 'ADD_BALLS', payload: { balls: [b] } });
        }
      });
      setBallCount(ballsRef.current.length);
    }

    BasicModesLogic.enforceGlobalBoundaries(ballsRef.current, effectiveCfg, rotRad, morphData, containerRadiusRef.current);

    draw();
  }, [isPlaying, initSimulation, draw]);

  return { update };
};
